import React, { useState, useEffect } from 'react';
import { Card } from '../Card';
import { SummaryStats, PricingModelOption } from '../../types';
import { formatNumber, formatCurrency, formatPercentage } from '../../utils/formatters';
import { formatModelLabel, modelHasValue } from '../../utils/pricing';
import './SummaryCards.css';

interface SummaryCardsProps {
  stats: SummaryStats;
  loading?: boolean;
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
  totalSbCost?: number;
  accountPricingModel?: { label: string; model_type?: string; model_value?: number } | null;
  pricingModelOptions?: PricingModelOption[];
  onSaveAccountPricing?: (modelType: string, modelValue: number) => Promise<void>;
}

export const SummaryCards: React.FC<SummaryCardsProps> = ({
  stats,
  loading,
  isAdmin,
  isSuperAdmin,
  totalSbCost,
  accountPricingModel,
  pricingModelOptions = [],
  onSaveAccountPricing,
}) => {
  const [editing, setEditing] = useState(false);
  const [editType, setEditType] = useState<string>('');
  const [editValue, setEditValue] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // Reset edit state when the displayed model changes (e.g., after save or
  // account switch) so the form picks up the new defaults next time it opens.
  useEffect(() => {
    if (!editing) {
      setEditType(accountPricingModel?.model_type ?? '');
      setEditValue(String(accountPricingModel?.model_value ?? 0));
    }
  }, [accountPricingModel, editing]);

  if (loading) {
    return (
      <div className="summary-cards">
        <div className="loading-placeholder">Loading...</div>
      </div>
    );
  }

  const roiClass = 'positive';
  const canEdit = isAdmin && !!onSaveAccountPricing && pricingModelOptions.length > 0;

  const startEdit = () => {
    setEditType(accountPricingModel?.model_type ?? pricingModelOptions[0]?.model_type ?? '');
    setEditValue(String(accountPricingModel?.model_value ?? 0));
    setEditing(true);
  };

  const cancelEdit = () => setEditing(false);

  const submitEdit = async () => {
    if (!onSaveAccountPricing || !editType) return;
    const numValue = editType === 'free' ? 0 : parseFloat(editValue);
    if (editType !== 'free' && isNaN(numValue)) return;
    setSaving(true);
    try {
      await onSaveAccountPricing(editType, numValue);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const onTypeChange = (newType: string) => {
    const opt = pricingModelOptions.find(o => o.model_type === newType);
    setEditType(newType);
    // If switching to a different model type, seed with that type's default;
    // if switching back to the current type, restore the current value.
    if (newType === accountPricingModel?.model_type) {
      setEditValue(String(accountPricingModel?.model_value ?? 0));
    } else {
      setEditValue(String(opt?.default_value ?? 0));
    }
  };

  const previewLabel = editType
    ? formatModelLabel(editType, parseFloat(editValue) || 0)
    : '';

  return (
    <div className="summary-cards">
      <Card
        title="Donations"
        value={formatNumber(stats.totalDonations)}
      />
      <Card
        title="Amount Raised"
        value={formatCurrency(stats.totalRaised)}
      />
      {isSuperAdmin && totalSbCost !== undefined && (
        <Card
          title="Cost Estimate"
          value={formatCurrency(totalSbCost)}
        />
      )}
      <Card
        title="Your Cost"
        value={formatCurrency(stats.totalCost)}
      />
      <Card
        title="Your ROI"
        value={formatPercentage(stats.roiPercentage)}
        valueClassName={roiClass}
      />
      {accountPricingModel && (
        <div className="card pricing-model-card">
          <h3 className="card-title">Current Pricing Model</h3>
          {editing ? (
            <>
              <div className="pricing-model-controls">
                <select
                  className="pricing-model-select"
                  value={editType}
                  onChange={(e) => onTypeChange(e.target.value)}
                >
                  {pricingModelOptions.map(opt => (
                    <option key={opt.model_type} value={opt.model_type}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {modelHasValue(editType) && (
                  <input
                    className="pricing-model-input"
                    type="number"
                    step="any"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submitEdit();
                      if (e.key === 'Escape') cancelEdit();
                    }}
                    autoFocus
                  />
                )}
                <button
                  className="pricing-model-save-btn"
                  disabled={saving}
                  onClick={submitEdit}
                >
                  {saving ? '...' : 'Save'}
                </button>
                <button
                  className="pricing-model-cancel-btn"
                  disabled={saving}
                  onClick={cancelEdit}
                >
                  Cancel
                </button>
              </div>
              {previewLabel && (
                <div className="pricing-model-preview">→ {previewLabel}</div>
              )}
            </>
          ) : (
            <div className="pricing-model-display">
              <div className="card-value">{accountPricingModel.label}</div>
              {canEdit && (
                <button
                  className="pricing-model-edit-btn"
                  onClick={startEdit}
                  title="Change pricing model"
                  aria-label="Edit pricing model"
                >
                  ✎
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
